import { Red5Player } from '@optools/components/players/red5-player';
import { Badge } from '@optools/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { cn } from '@optools/components/ui/utils';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { memo, ReactNode, useCallback, useRef, useState } from 'react';

type Red5Config = LoggerGamePipeline['red5Config'];

export interface LoggerRed5PlayersProps {
  config: Red5Config;
  debugMode: boolean;
  onFailure: () => void;
}

export const LoggerRed5Players = memo(
  ({ config, debugMode, onFailure }: LoggerRed5PlayersProps) => {
    const handleOnFailure = useCallback(() => {
      onFailure();
    }, [onFailure]);

    if (!config) {
      return <p>There is no Red5 Player Config associated with this feed</p>;
    }

    const { postProcStreamName, preProcStreamName, ...hostAndNodeGroups } = config;

    return (
      <section className="flex w-full flex-col gap-4">
        <Player
          debugMode={debugMode}
          streamName={preProcStreamName}
          onFailure={handleOnFailure}
          {...hostAndNodeGroups}>
          <Badge>PREPROC</Badge>
        </Player>

        <Player
          debugMode={debugMode}
          streamName={postProcStreamName}
          onFailure={handleOnFailure}
          {...hostAndNodeGroups}>
          <Badge variant="outline">POSTPROC</Badge>
        </Player>
      </section>
    );
  },
);

LoggerRed5Players.displayName = 'LoggerRed5Players';

type PlayerMode = 'primary' | 'backup';

export const PLAYER_COOLDOWN_TIME = 30000;

interface PlayerProps extends Pick<
  NonNullable<Red5Config>,
  'primaryHost' | 'primaryNodeGroup' | 'backupHost' | 'backupNodeGroup'
> {
  children: ReactNode;
  debugMode: boolean;
  onFailure: () => void;
  streamName: string | null | undefined;
}

const Player = ({
  backupHost,
  backupNodeGroup,
  children,
  debugMode,
  onFailure,
  primaryHost,
  primaryNodeGroup,
  streamName,
}: PlayerProps) => {
  const [mode, setMode] = useState<PlayerMode>('primary');
  const cooldownRef = useRef<{ [key in PlayerMode]: number | undefined }>({
    backup: undefined,
    primary: undefined,
  });

  const host = mode === 'primary' ? primaryHost : backupHost;
  const nodeGroup = mode === 'primary' ? primaryNodeGroup : backupNodeGroup;

  const handleSubscribeError = useCallback(() => {
    const currentMode = mode;
    const targetMode: PlayerMode = currentMode === 'primary' ? 'backup' : 'primary';

    clearTimeout(cooldownRef.current[currentMode]);

    const cooldownId = setTimeout(() => {
      cooldownRef.current = {
        ...cooldownRef.current,
        [mode]: undefined,
      };
    }, PLAYER_COOLDOWN_TIME);

    cooldownRef.current = {
      ...cooldownRef.current,
      [currentMode]: cooldownId,
    };

    if (cooldownRef.current[targetMode]) {
      onFailure();
    } else {
      setMode(targetMode);
    }
  }, [mode, onFailure]);

  return (
    <figure>
      <figcaption className={cn('mb-2', debugMode && 'justify-between')}>
        <div className="mb-2 flex items-center justify-between gap-2">
          {children}{' '}
          <Select value={mode} onValueChange={(value) => setMode(value as PlayerMode)}>
            <SelectTrigger aria-label="Player Mode">
              {mode === 'primary' ? (
                <CircleCheck className="text-green-500 dark:text-green-600" />
              ) : (
                <TriangleAlert className="text-yellow-500 dark:text-yellow-600" />
              )}
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="primary">Primary</SelectItem>
              <SelectItem value="backup">Backup</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {debugMode && (
          <div className="full flex-1 rounded border border-dashed border-foreground p-1 font-mono text-xs">
            <p>Host: {host}</p>
            <p>Stream Name: {streamName}</p>
            <p>Node Group: {nodeGroup}</p>
          </div>
        )}
      </figcaption>

      <Red5Player
        key={mode}
        className="w-full object-cover"
        host={host ?? ''}
        nodeGroup={nodeGroup ?? ''}
        streamName={streamName ?? ''}
        onSubscribeError={handleSubscribeError}
      />
    </figure>
  );
};
